// Llamadas de la tienda con sesión. Todas van con el negocio elegido (`cliente_id`).
import type { MedioCobro } from '../compartido';
import { api } from './api';
import { vendedorDeSesion } from './sesion';
import type {
  Articulo,
  Categoria,
  Frecuentes,
  ItemPedido,
  Pagina,
  PedidoDetalle,
  PedidoResumen,
  Revision,
} from './tipos';

export const TAMANO_PAGINA = 40;

export const catalogo = (
  negocioId: string,
  f: { q?: string; categoria?: string | null; cursor?: string | null },
  signal?: AbortSignal,
) =>
  api<Pagina<Articulo>>('/tienda/catalogo', {
    consulta: {
      cliente_id: negocioId,
      q: f.q?.trim(),
      categoria: f.categoria,
      cursor: f.cursor,
      limite: f.q?.trim() ? 60 : TAMANO_PAGINA,
    },
    signal,
  });

export const categorias = (negocioId: string) =>
  api<{ datos: Categoria[] }>('/tienda/categorias', { consulta: { cliente_id: negocioId } });

export const frecuentes = (negocioId: string) =>
  api<Frecuentes>('/tienda/frecuentes', { consulta: { cliente_id: negocioId } });

export const revisarCarrito = (negocioId: string, items: ItemPedido[], signal?: AbortSignal) =>
  api<Revision>('/tienda/carrito', { cuerpo: { cliente_id: negocioId, items }, signal });

export interface PedidoNuevo {
  items: ItemPedido[];
  cobro?: MedioCobro | null;
  fecha_entrega?: string | null;
  observaciones?: string;
  clave: string;
  /** Modo vendedor (decisión D60): cuándo se tomó, si quedó guardado sin señal y se manda después. */
  hora?: string;
}

export const confirmarPedido = (negocioId: string, p: PedidoNuevo) =>
  api<PedidoDetalle>('/tienda/pedidos', {
    cuerpo: {
      cliente_id: negocioId,
      items: p.items,
      ...(p.cobro && { cobro: p.cobro }),
      ...(p.fecha_entrega && { fecha_entrega: p.fecha_entrega }),
      ...(p.observaciones?.trim() && { observaciones: p.observaciones.trim() }),
      clave: p.clave,
      ...(p.hora && { hora: p.hora }),
    },
    // Con mala señal la respuesta puede tardar: se espera más antes de dar por perdido el intento (se reintenta con
    // la misma clave, así que no se duplica).
    timeoutMs: 30_000,
  });

export const misPedidos = (negocioId: string, cursor?: string | null) =>
  api<Pagina<PedidoResumen>>('/tienda/pedidos', { consulta: { cliente_id: negocioId, limite: 20, cursor } });

/**
 * Un pedido. El preventista (decisión D60) no lo modifica ni escribe por el comercio: eso queda para el comercio y para
 * la distribuidora.
 */
const paraVendedor = (p: PedidoDetalle): PedidoDetalle =>
  vendedorDeSesion() ? { ...p, puede_modificar: false, chat: false } : p;

export const pedido = async (id: string) =>
  paraVendedor(await api<PedidoDetalle>(`/tienda/pedidos/${encodeURIComponent(id)}`));

export const cancelarPedido = async (id: string) =>
  paraVendedor(
    await api<PedidoDetalle>(`/tienda/pedidos/${encodeURIComponent(id)}/cancelar`, { metodo: 'POST', cuerpo: {} }),
  );

/**
 * Modificar un pedido que espera confirmación (decisión D52): el mismo pedido, con la versión que se tenía a la vista.
 * Si la distribuidora lo cambió mientras tanto vuelve PEDIDO_CAMBIO ("El pedido cambió, revisalo").
 */
export const modificarPedido = (
  id: string,
  negocioId: string,
  p: { version: number; items: ItemPedido[]; cobro?: MedioCobro | null; observaciones?: string | null },
) =>
  api<PedidoDetalle>(`/tienda/pedidos/${encodeURIComponent(id)}`, {
    metodo: 'PUT',
    cuerpo: {
      cliente_id: negocioId,
      version: p.version,
      items: p.items,
      ...(p.cobro && { cobro: p.cobro }),
      ...(p.observaciones !== undefined && { observaciones: p.observaciones ?? '' }),
    },
    timeoutMs: 30_000,
  });
